
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import BrandFilterBar from "../../components/common/BrandFilterBar/BrandFilterBar";
import Breadcrumbs from "../../components/common/Breadcrumbs/Breadcrumbs";
import Footer from "../../components/common/Footer/Footer";
import { getAllAgencyUser } from "../../store/agencyStore/actions/getAllAgencyUserActions";
import { FaRegEdit } from "react-icons/fa";
import { useToast } from "../../hooks/useToast";


// get bg color of active and inactive
const getStatusStyle = (status) => {
    switch (status.toLowerCase()) {
        case 'active':
            return 'bg-[#0D9B50] text-[#FFFFFF]';
        case 'inactive':
            return 'bg-[#FF0505] text-[#FFFFFF]';
        default:
            return 'bg-[#FCBE2D] text-[#FFFFFF]';
    }
};

// New Campaign Card component for mobile view
const CampaignCard = ({ campaign, onViewDetails }) => (
    <div className="bg-white rounded-lg shadow-md p-4 mb-4 border border-gray-300">
        <div className="flex flex-col space-y-3">
            <div>
                <span className="text-[1.6rem] font-medium">{campaign.firstName}</span>
            </div>

            <div className="flex items-center">
                <span className="text-[1.4rem] font-semibold text-gray-500 w-32">Last Name:</span>
                <span className="text-[1.4rem]">{campaign.lastName}</span>
            </div>

            <div className="flex items-center">
                <span className="text-[1.4rem] font-semibold text-gray-500 w-32">Email:</span>
                <span className="text-[1.4rem]">{campaign.email}</span>
            </div>

            <div className="flex items-center">
                <span className="text-[1.4rem] font-semibold text-gray-500 w-32">Phone Number:</span>
                <span className="text-[1.4rem]">{campaign.phone}</span>
            </div>


            <div className="flex items-center">
                <span className="text-[1.4rem] font-semibold text-gray-500 w-32">Role:</span>
                <span className="text-[1.4rem]">{campaign.role}</span>
            </div>

            <div className="flex items-center">
                <span className="text-[1.4rem] font-semibold text-gray-500 w-32">Status:</span>
                <span className={`text-[1.4rem] px-2 py-1 rounded-full ${getStatusStyle(campaign.status)}`}>
                    {campaign.status}
                </span>
            </div>

            <div className="mt-2">
                {campaign.role !== "Admin" && <button
                    className={`text-black text-[1.4rem] px-4 py-2 rounded w-full`}
                    onClick={() => onViewDetails(campaign)}
                >
                    <FaRegEdit size={20} color="black" />
                </button>}
            </div>
        </div>
    </div>
);

const AgencyUsersListPage = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const Toast = useToast();

    const [isMobile, setIsMobile] = useState(false);
    const [filteredUsers, setFilteredUsers] = useState([]);

    const customTableStyles = {
        table: {
            style: {
                position: 'relative',
            },
        },
        headRow: {
            style: {
                backgroundColor: '#F1F4F9',
                minHeight: '52px',
                fontWeight: 700,
                borderRadius: '0.4rem',
                borderBottom: 'none',
                // position: 'fixed',
                top: 0,
            },
            denseStyle: {
                minHeight: '32px',
            },
        },
        rows: {
            style: {
                fontSize: '1.4rem',  // Increased text size for rows
                fontWeight: 600,
                color: '#202224',
                borderBottom: '1px solid #EDEFF1',
                minHeight: '70px',
            },
            denseStyle: {
                minHeight: '40px',
            },
        },
        headCells: {
            style: {
                padding: '1rem',
                fontSize: '1.6rem',
                textAlign: 'left',
            },
        },
        cells: {
            style: {
                padding: '1.5rem',
            },
        },
    };

    const columns = [
        {
            name: 'First Name',
            selector: row => row.firstName,
            sortable: true,
            style: {
                width: '10rem',  // Set a smaller width for this column
            },
            wrap: true,
        },
        {
            name: 'Last Name',
            selector: row => row.lastName,
            sortable: true,
            style: {
                width: '10rem',  // Adjust the width as needed
            },
        },
        {
            name: 'Email',
            selector: row => row.email,
            style: {
                width: '10rem',  // Set a smaller width for this column
            },
        },
        {
            name: 'Phone Number',
            selector: row => row.phone,
            style: {
                width: '10rem',  // Adjust the width for consistency
            },
        },
        {
            name: 'Role',
            selector: row => row.role,
            style: {
                width: '10rem',
            },
        },
        {
            name: 'Status',
            selector: row => (
                <span
                    className={`${getStatusStyle(row.status)} w-full min-h-[2rem] sm:text-sm md:text-[1.4rem] px-3 rounded-full text-center flex justify-center items-center `}
                >
                    <span className="truncate">
                        {row.status}
                    </span>
                </span>
            ),
            style: {
                width: '10rem',
            },
        },
        {
            name: 'Action',
            cell: row => row.role !== "Admin" && <button
                className="bg-white text-white px-4 py-2 rounded"
                onClick={() => navigate(`/agency/user-management/add-edit`, { state: { user: row } })}
            >
                <FaRegEdit className="text-black" size={20}/>
            </button>,
            ignoreRowClick: true,
            allowOverflow: true,
            style: {
                width: '10rem',  // Adjust width for the Action column
            },
        },
    ];

    const handleFilter = (role) => {
        console.log("role", role);
        let roleValue = role == 1 ? "Manager" : role == 2 ? "Viewer" : "";
        dispatch(getAllAgencyUser({ role: roleValue }, (status, response, message) => {
            if (status === 200) {
                setFilteredUsers(response);
            } else {
                setFilteredUsers([]);
            }
        }));
    };

    const handleReset = () => {
        dispatch(getAllAgencyUser({ role: '' }, (status, response, message) => {
            if (status === 200) {
                setFilteredUsers(response);
            } else {
                setFilteredUsers([]);
            }
        }));
    };

    // Check if the screen is mobile size
    useEffect(() => {
        const checkIfMobile = () => {
            setIsMobile(window.innerWidth < 768); // 768px is typical tablet breakpoint
        };

        // Set initial value
        checkIfMobile();

        // Add event listener for window resize
        window.addEventListener('resize', checkIfMobile);

        // Clean up
        return () => window.removeEventListener('resize', checkIfMobile);
    }, []);

    useEffect(() => {
        dispatch(getAllAgencyUser({ role: '' }, (status, response, message) => {
            if (status === 200) {
                setFilteredUsers(response);
            } else {
                setFilteredUsers([]);
            }
        }));
    }, []);

    return (
        <div className="w-full h-full flex flex-col justify-start bg-[#F2F3F7] ">
            <Breadcrumbs breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'User Management', href: '/agency/user-management' }]} />
            <div className="w-full flex justify-between items-center my-[0.5rem] px-[4rem] h-[9rem]">
                <div className="relative py-[1.5rem] text-[1.6rem]">
                    {/* User Management */}
                </div>
                <div className="flex space-x-[1.2rem]">
                    <button
                        className="flex items-center bg-[#0D9B50] rounded-lg text-[1.6rem] text-white px-4 py-3"
                        onClick={() => {
                            navigate('/agency/user-management/add-edit', { state: { user: null } });
                        }}
                    >
                        <h1 className="inter-font font-[500] px-4 py-2">Create New User</h1>
                    </button>
                </div>
            </div>
            <div className='w-full h-[12rem] flex flex-col px-[4rem]'>
                <BrandFilterBar
                    dataList={[{name:"Manager", brandId: "1"}, {name:"Viewer", brandId: "2"}]}
                    optionLabel="All Roles"
                    onFilter={handleFilter}
                    onReset={handleReset}
                />
            </div>
            <div className="w-full h-full flex flex-col px-[4rem]">
                <div className="w-full flex flex-col rounded-[2.6rem] bg-[#ffffff]">
                    {/* Sticky Header Section */}
                    <div className="sticky top-0 z-10 bg-[#ffffff] mb-1 p-[3.2rem]">
                        <h1 className="text-[2.4rem] text-[#202224] inter-font font-[700]">
                            User Management
                        </h1>
                    </div>

                    {/* Table Container or Card Container based on screen size */}
                    <div className="w-full max-h-[50rem] overflow-y-auto no-scrollbar px-[2.4rem] pb-[2.4rem]">
                        {Array.isArray(filteredUsers) && filteredUsers.length > 0 ? (
                            isMobile ? (
                                // Mobile view - Cards
                                <div className="flex flex-col space-y-4">
                                    {filteredUsers.map((user, index) => (
                                        <CampaignCard
                                            key={index}
                                            campaign={user}
                                            onViewDetails={() => navigate(`/agency/user-management/add-edit`, { state: { user } })}
                                        />
                                    ))}
                                </div>
                            ) : (
                                // Desktop view - Table
                                <DataTable
                                    columns={columns}
                                    data={filteredUsers}
                                    customStyles={customTableStyles}
                                    pagination
                                    highlightOnHover
                                    responsive
                                />
                            )
                        ) : (
                            <div className="text-2xl md:text-4xl leading-[50px] md:leading-[70px] text-gray-500 text-center mt-4">
                                There are no records to display.
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    )
}

export default AgencyUsersListPage
